import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { PDFDocument, PDFName, PDFRadioGroup, PDFTextField, PDFCheckBox, PDFDropdown } from 'pdf-lib';
// Use the webpack entry point which bundles the worker locally — no CDN needed
// @ts-ignore — no type declarations for this entry point
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs';
import { apiFetchResponse, isApiUrl } from '../lib/apiFetch';

interface PDFField {
  name: string;
  type: 'text' | 'checkbox' | 'dropdown';
  value: string | boolean;
  rect?: { x: number; y: number; width: number; height: number };
  pageIndex: number;
  // Selectable options for dropdown (choice) fields.
  options?: string[];
  // For widgets that are part of a radio group but rendered as independent
  // checkboxes (so the user can mark zero, one, or multiple options — the
  // form's mutual-exclusivity isn't enforced in the preview).
  radioGroupName?: string;
  radioOnValue?: string;
  radioWidgetIndex?: number;
}

interface PDFFormEditorProps {
  pdfUrl: string;
  onPdfReady?: (pdfBytes: Uint8Array) => void;
  onFieldsChange?: (fields: PDFField[]) => void;
}

export const PDFFormEditor: React.FC<PDFFormEditorProps> = ({
  pdfUrl,
  onPdfReady,
  onFieldsChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocument | null>(null);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [fields, setFields] = useState<PDFField[]>([]);
  const [pageImages, setPageImages] = useState<string[]>([]);
  const scale = 1.5;
  const [pageDimensions, setPageDimensions] = useState<{ width: number; height: number }[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load PDF and extract fields
  useEffect(() => {
    let cancelled = false;
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch PDF. Backend documents are read with the staff member's token —
        // the editor fetches the bytes itself, so no blob: hand-off is needed for
        // the routes to stop being @Public(). A blob:/foreign URL is fetched as-is.
        const response = isApiUrl(pdfUrl)
          ? await apiFetchResponse(pdfUrl)
          : await fetch(pdfUrl);
        if (cancelled) return;
        
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        setPdfBytes(bytes);

        // Load with pdf-lib for field manipulation
        const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        setPdfDoc(doc);

        // Extract form fields
        const form = doc.getForm();
        const formFields = form.getFields();
        const extractedFields: PDFField[] = [];

        formFields.forEach((field) => {
          const name = field.getName();
          const widgets = field.acroField.getWidgets();
          
          widgets.forEach((widget, widgetIndex) => {
            const rect = widget.getRectangle();
            const pageRef = widget.P();
            let pageIndex = 0;
            
            if (pageRef) {
              const pages = doc.getPages();
              pageIndex = pages.findIndex(page => page.ref === pageRef);
              if (pageIndex === -1) pageIndex = 0;
            }

            if (field instanceof PDFTextField) {
              extractedFields.push({
                name: widgetIndex > 0 ? `${name}_${widgetIndex}` : name,
                type: 'text',
                value: field.getText() || '',
                rect: rect ? {
                  x: rect.x,
                  y: rect.y,
                  width: rect.width,
                  height: rect.height,
                } : undefined,
                pageIndex,
              });
            } else if (field instanceof PDFCheckBox) {
              extractedFields.push({
                name: widgetIndex > 0 ? `${name}_${widgetIndex}` : name,
                type: 'checkbox',
                value: field.isChecked(),
                rect: rect ? {
                  x: rect.x,
                  y: rect.y,
                  width: rect.width,
                  height: rect.height,
                } : undefined,
                pageIndex,
              });
            } else if (field instanceof PDFRadioGroup) {
              // Render each radio-button widget as its own independent checkbox.
              // The on-value is the widget's /AP/N key that isn't /Off.
              const AP = widget.dict.get(PDFName.of('AP')) as any;
              const N = AP?.get?.(PDFName.of('N'));
              let onValue = '';
              if (N?.keys) {
                for (const key of N.keys()) {
                  const k = key.toString();
                  if (k !== '/Off') { onValue = k.replace(/^\//, ''); break; }
                }
              }
              const asEntry = widget.dict.get(PDFName.of('AS'));
              const isChecked = !!asEntry && asEntry.toString() === `/${onValue}` && onValue !== '';
              extractedFields.push({
                name: `${name}__w${widgetIndex}`,
                type: 'checkbox',
                value: isChecked,
                rect: rect ? {
                  x: rect.x,
                  y: rect.y,
                  width: rect.width,
                  height: rect.height,
                } : undefined,
                pageIndex,
                radioGroupName: name,
                radioOnValue: onValue,
                radioWidgetIndex: widgetIndex,
              });
            } else if (field instanceof PDFDropdown) {
              // Choice field — rendered as a <select> overlay. The page image
              // is rendered with annotations disabled, so without this overlay
              // the selected value (set server-side) would be invisible.
              const selected = field.getSelected();
              extractedFields.push({
                name: widgetIndex > 0 ? `${name}_${widgetIndex}` : name,
                type: 'dropdown',
                value: (Array.isArray(selected) ? selected[0] : selected) || '',
                options: field.getOptions(),
                rect: rect ? {
                  x: rect.x,
                  y: rect.y,
                  width: rect.width,
                  height: rect.height,
                } : undefined,
                pageIndex,
              });
            }
          });
        });

        setFields(extractedFields);
        onFieldsChange?.(extractedFields);

        // Render PDF pages as images using pdfjs-dist
        // Copy bytes because pdfjs may transfer the ArrayBuffer to its web worker, detaching the original
        const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(bytes) });
        const pdfJsDoc = await loadingTask.promise;
        if (cancelled) return;
        const images: string[] = [];
        const dimensions: { width: number; height: number }[] = [];

        for (let i = 1; i <= pdfJsDoc.numPages; i++) {
          if (cancelled) return;
          // Per-page try/catch so a transient pdfjs error (e.g. "Invalid page
          // request" when the worker is briefly unavailable) doesn't kill the
          // whole load. We push a placeholder + zero dimension and keep going;
          // visible pages will still render correctly.
          try {
            const page = await pdfJsDoc.getPage(i);
            const viewport = page.getViewport({ scale });
            dimensions.push({ width: viewport.width, height: viewport.height });

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d')!;
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({
              canvasContext: context,
              viewport,
              canvas,
              // Disable annotation rendering so form field values don't appear in background
              annotationMode: 0, // 0 = DISABLE, 1 = ENABLE, 2 = ENABLE_FORMS, 3 = ENABLE_STORAGE
            }).promise;

            images.push(canvas.toDataURL('image/png'));
          } catch (pageErr) {
            console.warn(`PDFFormEditor: skipping page ${i}:`, pageErr);
            images.push('');
            dimensions.push({ width: 612, height: 792 });
          }
        }

        if (cancelled) return;
        setPageImages(images);
        setPageDimensions(dimensions);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading PDF:', err);
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
        setLoading(false);
      }
    };

    if (pdfUrl) {
      void loadPdf();
    }
    return () => {
      cancelled = true;
    };
  }, [pdfUrl, scale]);

  // Handle field value change
  const handleFieldChange = useCallback(async (fieldName: string, newValue: string | boolean) => {
    if (!pdfDoc) return;

    try {
      const form = pdfDoc.getForm();
      const current = fields.find(f => f.name === fieldName);

      // Radio-widget rendered as an independent checkbox: toggle that widget's
      // /AS directly so siblings in the same group stay untouched (bypasses
      // pdf-lib's PDFRadioGroup.select which enforces mutual exclusivity).
      if (
        current?.radioGroupName !== undefined &&
        current.radioOnValue !== undefined &&
        current.radioWidgetIndex !== undefined &&
        typeof newValue === 'boolean'
      ) {
        const radioGroup = form.getRadioGroup(current.radioGroupName);
        const widget = radioGroup.acroField.getWidgets()[current.radioWidgetIndex];
        if (widget && current.radioOnValue) {
          widget.dict.set(
            PDFName.of('AS'),
            PDFName.of(newValue ? current.radioOnValue : 'Off'),
          );
          // Keep the radio group's field value (/V) in sync with the widget's
          // appearance state. The /AS we set above only drives how a plain PDF
          // viewer paints the widget — DocuSeal (and any compliant flattener)
          // re-derives the selected option from /V, not /AS. Without this the
          // agent's checkbox/radio edits silently revert on the signed PDF that
          // comes back (the stale auto-filled /V wins), while text fields — which
          // set /V via setText — survive. We set /V directly on the field dict
          // rather than via radioGroup.select(), so sibling widgets' /AS isn't
          // clobbered and the independent-checkbox preview is preserved.
          if (newValue) {
            radioGroup.acroField.dict.set(
              PDFName.of('V'),
              PDFName.of(current.radioOnValue),
            );
          } else {
            // Unchecking: if another widget in this group is still on, point /V
            // at its on-value; otherwise clear the group to Off.
            const stillOn = fields.find(
              f =>
                f.radioGroupName === current.radioGroupName &&
                f.name !== fieldName &&
                f.value === true &&
                !!f.radioOnValue,
            );
            radioGroup.acroField.dict.set(
              PDFName.of('V'),
              PDFName.of(stillOn?.radioOnValue || 'Off'),
            );
          }
        }
      } else {
        // Remove widget index suffix to get original field name
        const originalFieldName = fieldName.replace(/_\d+$/, '');
        const field = form.getField(originalFieldName);

        if (field instanceof PDFTextField && typeof newValue === 'string') {
          field.setText(newValue);
        } else if (field instanceof PDFCheckBox && typeof newValue === 'boolean') {
          if (newValue) {
            field.check();
          } else {
            field.uncheck();
          }
        } else if (field instanceof PDFDropdown && typeof newValue === 'string') {
          if (newValue) {
            try { field.select(newValue); } catch { /* not in option list */ }
          } else {
            field.clear();
          }
        }
      }

      // Update local state
      setFields(prev => prev.map(f =>
        f.name === fieldName ? { ...f, value: newValue } : f
      ));

      // Generate updated PDF bytes
      const updatedBytes = await pdfDoc.save();
      setPdfBytes(updatedBytes);
      onPdfReady?.(updatedBytes);
    } catch (err) {
      console.error('Error updating field:', err);
    }
  }, [pdfDoc, onPdfReady, fields]);

  // Get flattened PDF bytes
  const getFlattenedPdf = useCallback(async (): Promise<Uint8Array | null> => {
    if (!pdfDoc) return null;

    try {
      // Clone the document to avoid modifying the original
      const clonedDoc = await PDFDocument.load(await pdfDoc.save());
      const form = clonedDoc.getForm();
      form.flatten();
      return await clonedDoc.save();
    } catch (err) {
      console.error('Error flattening PDF:', err);
      return null;
    }
  }, [pdfDoc]);

  // Expose getFlattenedPdf via ref or callback
  useEffect(() => {
    if (pdfDoc && pdfBytes) {
      onPdfReady?.(pdfBytes);
    }
  }, [pdfDoc, pdfBytes, onPdfReady]);

  // Convert PDF coordinates to screen coordinates
  const pdfToScreenCoords = (
    rect: { x: number; y: number; width: number; height: number },
    pageIndex: number
  ) => {
    if (!pageDimensions[pageIndex]) return null;
    
    const pdfPage = pdfDoc?.getPages()[pageIndex];
    if (!pdfPage) return null;
    
    const { width: pdfWidth, height: pdfHeight } = pdfPage.getSize();
    const { width: screenWidth, height: screenHeight } = pageDimensions[pageIndex];
    
    const scaleX = screenWidth / pdfWidth;
    const scaleY = screenHeight / pdfHeight;
    
    // PDF coordinates have origin at bottom-left, screen at top-left
    return {
      left: rect.x * scaleX,
      top: screenHeight - (rect.y + rect.height) * scaleY,
      width: rect.width * scaleX,
      height: rect.height * scaleY,
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        width: '100%', 
        height: '100%', 
        overflow: 'auto',
        bgcolor: '#525659',
        p: 2,
      }}
    >
      {pageImages.map((image, pageIndex) => (
        <Paper
          key={pageIndex}
          sx={{
            position: 'relative',
            mb: 2,
            mx: 'auto',
            width: pageDimensions[pageIndex]?.width || 'auto',
            height: pageDimensions[pageIndex]?.height || 'auto',
            boxShadow: 3,
          }}
        >
          {/* PDF page as background */}
          <img
            src={image}
            alt={`Page ${pageIndex + 1}`}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
            }}
          />
          
          {/* Overlay editable fields */}
          {fields
            .filter(field => field.pageIndex === pageIndex && field.rect)
            .map((field) => {
              const screenCoords = pdfToScreenCoords(field.rect!, pageIndex);
              if (!screenCoords) return null;

              if (field.type === 'text') {
                return (
                  <input
                    key={field.name}
                    type="text"
                    value={field.value as string}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    style={{
                      position: 'absolute',
                      left: screenCoords.left,
                      top: screenCoords.top,
                      width: screenCoords.width,
                      height: screenCoords.height,
                      border: '1px solid #ccc',
                      background: '#fff',
                      fontSize: `${Math.max(10, screenCoords.height * 0.7)}px`,
                      fontFamily: 'Arial, sans-serif',
                      padding: '0 2px',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '1px solid #1976d2';
                      e.target.style.boxShadow = '0 0 3px rgba(25, 118, 210, 0.5)';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '1px solid #ccc';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                );
              } else if (field.type === 'dropdown') {
                return (
                  <select
                    key={field.name}
                    value={field.value as string}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    style={{
                      position: 'absolute',
                      left: screenCoords.left,
                      top: screenCoords.top,
                      width: screenCoords.width,
                      height: screenCoords.height,
                      border: '1px solid #ccc',
                      background: '#fff',
                      fontSize: `${Math.max(10, screenCoords.height * 0.6)}px`,
                      fontFamily: 'Arial, sans-serif',
                      padding: '0 2px',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '1px solid #1976d2';
                      e.target.style.boxShadow = '0 0 3px rgba(25, 118, 210, 0.5)';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '1px solid #ccc';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value=""></option>
                    {(field.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                );
              } else if (field.type === 'checkbox') {
                return (
                  <div
                    key={field.name}
                    style={{
                      position: 'absolute',
                      left: screenCoords.left,
                      top: screenCoords.top,
                      width: screenCoords.width,
                      height: screenCoords.height,
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={field.value as boolean}
                      onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                      style={{
                        width: Math.min(screenCoords.width, screenCoords.height) * 0.8,
                        height: Math.min(screenCoords.width, screenCoords.height) * 0.8,
                        margin: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                );
              }
              return null;
            })}
        </Paper>
      ))}
    </Box>
  );
};

// Hook to get flattened PDF from editor
export const usePDFEditor = () => {
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const pdfDocRef = useRef<PDFDocument | null>(null);

  const handlePdfReady = useCallback((bytes: Uint8Array) => {
    setPdfBytes(bytes);
    void PDFDocument.load(bytes, { ignoreEncryption: true }).then(doc => {
      pdfDocRef.current = doc;
    }).catch(err => {
      console.error('Error loading PDF in editor hook:', err);
      pdfDocRef.current = null;
    });
  }, []);

  // Produce the exact bytes the user reviewed in the editor — the AcroForm
  // stays intact (despite the "flattened" name), per the comment on
  // downloadFlattenedPdf below. Both helpers go through this so the
  // downloaded PDF and the one we send to DocuSeal/save server-side are
  // byte-identical.
  const getFlattenedPdfBytes = useCallback(async (): Promise<Uint8Array> => {
    if (!pdfDocRef.current) {
      throw new Error('PDF document not loaded');
    }
    return pdfDocRef.current.save({ useObjectStreams: false });
  }, []);

  const downloadFlattenedPdf = useCallback(async (filename = 'document.pdf') => {
    try {
      // Keep the AcroForm intact with the user's filled values (don't flatten).
      // The RRQ upload portal reads AcroForm field values to validate
      // completeness — flattening strips the form and makes the portal report
      // "form is not completed" (or previously "empty") even though the text is
      // burned onto the page. `updateFieldAppearances: true` (pdf-lib default)
      // regenerates the visible appearances from the current values, and
      // `useObjectStreams: false` produces the classic xref-table wire format
      // that strict validators (government Java PDF parsers) require. This
      // mirrors what Mac Preview's "Export as PDF" produces, which was the
      // observed manual workaround.
      const flattenedBytes = await getFlattenedPdfBytes();

      // Download
      const blob = new Blob([flattenedBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading flattened PDF:', err);
      throw err;
    }
  }, [getFlattenedPdfBytes]);

  // Snapshot every field value in the currently-loaded PDF as a flat object
  // keyed by the PDF field name. Used by the "Ready" flow to send the agent's
  // confirmed state to the backend as overrides. Radio groups serialize to
  // their currently selected option's export value (or "" if nothing is
  // selected); checkboxes to boolean; text/dropdowns to string.
  const getFieldValues = useCallback((): Record<string, string | boolean> | null => {
    if (!pdfDocRef.current) return null;
    const form = pdfDocRef.current.getForm();
    const values: Record<string, string | boolean> = {};
    for (const field of form.getFields()) {
      const name = field.getName();
      try {
        if (field instanceof PDFTextField) {
          values[name] = field.getText() || '';
        } else if (field instanceof PDFCheckBox) {
          values[name] = field.isChecked();
        } else if (field instanceof PDFRadioGroup) {
          values[name] = field.getSelected() || '';
        }
        // Dropdowns: pdf-lib's PDFDropdown.getSelected() returns string[].
        // Use the first selection if any.
        else if ((field as any).getSelected) {
          const sel = (field as any).getSelected();
          values[name] = Array.isArray(sel) ? (sel[0] || '') : (sel || '');
        }
      } catch (err) {
        // Skip unreadable fields rather than failing the whole snapshot.
        console.warn(`getFieldValues: skipping ${name}:`, err);
      }
    }
    return values;
  }, []);

  return {
    pdfBytes,
    handlePdfReady,
    downloadFlattenedPdf,
    getFlattenedPdfBytes,
    getFieldValues,
  };
};

export default PDFFormEditor;

